import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, ApiError, NetworkError } from "./api";
import {
    WO_REGENERATE_TIMEOUT_MS,
    classifyRegenerateFailure,
    newestFilledId,
    regenerateFailureMessage,
    regenerateProgressLine,
} from "./wo-regenerate";

/** What the refill route answers with. */
export interface WoRegenerateResult {
    ok?: boolean;
    templateId?: string | null;
    filledAttachmentId?: string | null;
    /** False when the route answered 200 but produced no PDF (Issue #931). */
    generated?: boolean;
    fillWarnings?: string[];
}

/** The line a screen can show on the WO row after a regenerate settles. */
export interface WoRegenerateOutcomeLine {
    tone: "ok" | "warn" | "bad";
    text: string;
}

function settledLine(result: WoRegenerateResult): WoRegenerateOutcomeLine {
    const at = new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const warnings = result.fillWarnings ?? [];
    if (result.generated === false) {
        return {
            tone: "bad",
            text: warnings[0] ?? "No PDF was generated — this job has no locked return-PDF template to fill onto yet.",
        };
    }
    if (warnings.length > 0) {
        return {
            tone: "warn",
            text: `Regenerated ${at} with ${warnings.length} warning${warnings.length === 1 ? "" : "s"} — ${warnings[0]}`,
        };
    }
    return { tone: "ok", text: `Regenerated ${at} — review it, then send.` };
}

/**
 * One regenerate, told honestly (Issue #1413).
 *
 * Every "Regenerate" button in the app posts to the same route by design
 * (#658). This hook is the client half of that single seam: the request gets a
 * budget that matches the endpoint's real cost, the caller can show what is
 * happening while it runs, and a request that never came back is RE-CHECKED
 * against the job before anyone is told anything. Before this, the 30s default
 * deadline turned a slow success into "Couldn't regenerate the work order" and
 * a stale screen — and the office clicked again, running the whole fill twice.
 *
 * Each screen keeps its own success path (they invalidate different queries and
 * have their own wording); what lives here is the part that was lying.
 */
export function useWoRegenerate(opts: {
    /** Default job — screens with one job on screen pass it here. */
    jobId?: string | null;
    /** Screen's own success path: invalidations + its existing toast. */
    onSuccess?: (result: WoRegenerateResult) => void;
    /**
     * A request that LOOKED failed turned out to have landed. Refresh exactly
     * as on a normal success; the hook says the sentence, because that case
     * has its own.
     */
    onLandedAfterTimeout?: () => void;
}) {
    const { jobId: defaultJobId, onSuccess, onLandedAfterTimeout } = opts;
    const [startedAt, setStartedAt] = useState<number | null>(null);
    const [elapsedMs, setElapsedMs] = useState(0);
    const [lastOutcome, setLastOutcome] = useState<WoRegenerateOutcomeLine | null>(null);
    const inFlight = useRef(false);

    // Tick only while something is running — no timer on an idle screen.
    useEffect(() => {
        if (startedAt == null) return;
        setElapsedMs(Date.now() - startedAt);
        const id = window.setInterval(() => setElapsedMs(Date.now() - startedAt), 1000);
        return () => window.clearInterval(id);
    }, [startedAt]);

    /** The job's newest completed-WO attachment id, or null if we can't tell. */
    const readNewestFilledId = useCallback(async (id: string): Promise<{ id: string | null; failed: boolean }> => {
        try {
            const r = await api.get<{ attachments: Array<{ id: string; created_at?: string }> }>(
                `/api/attachments?jobId=${id}&category=work_order_filled`,
            );
            return { id: newestFilledId(r.attachments), failed: false };
        } catch {
            return { id: null, failed: true };
        }
    }, []);

    const start = useCallback(
        async (args: { jobId?: string | null; body?: Record<string, unknown> } = {}) => {
            const id = args.jobId ?? defaultJobId ?? null;
            if (!id || inFlight.current) return;
            inFlight.current = true;
            setLastOutcome(null);
            // Read the "before" state FIRST: it is what makes a re-check after a
            // timeout conclusive rather than a guess. A failure here is not
            // fatal — it only costs the ability to prove "landed" later.
            const before = await readNewestFilledId(id);
            setStartedAt(Date.now());
            try {
                const result = await api.post<WoRegenerateResult>(
                    `/api/office-review/jobs/${id}/refill-work-order`,
                    args.body ?? {},
                    { timeoutMs: WO_REGENERATE_TIMEOUT_MS },
                );
                setLastOutcome(settledLine(result));
                onSuccess?.(result);
            } catch (err) {
                const networkError = err instanceof NetworkError;
                const after = networkError ? await readNewestFilledId(id) : { id: null, failed: false };
                const outcome = classifyRegenerateFailure({
                    networkError,
                    filledIdBefore: before.failed ? undefined : before.id,
                    filledIdAfter: after.id,
                    recheckFailed: after.failed || before.failed,
                });
                if (outcome === "landed") {
                    setLastOutcome({ tone: "ok", text: "It did regenerate — review it, then send." });
                    onLandedAfterTimeout?.();
                    toast.success("It did regenerate — the new PDF is on the job. Review it, then send.");
                } else {
                    const { title, description } = regenerateFailureMessage(outcome);
                    // A refusal carries the server's own reason; keep it.
                    const serverMessage =
                        err instanceof ApiError &&
                        typeof err.body === "object" &&
                        err.body !== null &&
                        "message" in err.body
                            ? String((err.body as { message?: unknown }).message ?? "")
                            : "";
                    const headline = outcome === "rejected" && serverMessage ? serverMessage : title;
                    setLastOutcome({ tone: "bad", text: headline });
                    toast.error(headline, {
                        description: outcome === "rejected" && serverMessage ? undefined : description,
                    });
                }
            } finally {
                inFlight.current = false;
                setStartedAt(null);
                setElapsedMs(0);
            }
        },
        [defaultJobId, onLandedAfterTimeout, onSuccess, readNewestFilledId],
    );

    const isRunning = startedAt != null;
    return {
        start,
        isRunning,
        elapsedMs,
        /** The line the WO row shows while this runs, or null when idle. */
        progress: isRunning ? regenerateProgressLine(elapsedMs) : null,
        /** How the last regenerate ended, for screens that show it on the row. */
        lastOutcome: isRunning ? null : lastOutcome,
    };
}
